import React, { useEffect, useState } from 'react';
import { Mail, MapPin, Phone, Send, Linkedin, Dribbble, Twitter, Github, Check, ArrowUpRight, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { personalInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.trim().length < 10) newErrors.message = 'Message too short';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch("https://formspree.io/f/xykkkyoo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        toast({ title: 'Message sent!', description: "I'll get back to you soon." });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Please try again later.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: Linkedin, href: personalInfo.social.linkedin, label: 'LinkedIn' },
    { icon: Dribbble, href: personalInfo.social.dribbble, label: 'Dribbble' },
    { icon: Twitter, href: personalInfo.social.twitter, label: 'Twitter' },
    { icon: Github, href: personalInfo.social.github, label: 'GitHub' },
  ];

  return (
    <div className="relative">
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-6">Get in Touch</span>
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">Let's Start a <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#7c3aed]">Project</span></h1>
            <p className="text-white/50 text-xl leading-relaxed">Have a project in mind? I'd love to hear about it. Send me a message and I'll get back to you within 24-48 hours.</p>
          </div>
        </div>
      </section>
      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-2 space-y-10">
              <div className="reveal-on-scroll">
                <h2 className="text-2xl font-bold text-white mb-8">Contact Info</h2>
                <div className="space-y-6">
                  {[{ icon: Mail, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` }, { icon: Phone, label: 'Phone', value: personalInfo.phone, href: `tel:${personalInfo.phone}` }, { icon: MapPin, label: 'Location', value: personalInfo.location }].map((item) => (
                    <a key={item.label} href={item.href} className="group flex items-start gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-[#00ff88]/10 transition-colors"><item.icon className="w-6 h-6 text-[#00ff88]" /></div>
                      <div><p className="text-white/40 text-sm mb-1">{item.label}</p><p className="text-white font-medium group-hover:text-[#00ff88] transition-colors">{item.value}</p></div>
                    </a>
                  ))}
                </div>
              </div>
              <div className="reveal-on-scroll">
                <h2 className="text-2xl font-bold text-white mb-6">Follow Me</h2>
                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => (
                    <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="group w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center text-white/40 hover:bg-[#00ff88] hover:text-black transition-all duration-300"><social.icon size={22} /></a>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="glass-card p-8 lg:p-12">
                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-[#00ff88]/20 flex items-center justify-center"><Check className="w-12 h-12 text-[#00ff88]" /></div>
                    <h3 className="text-3xl font-bold text-white mb-4">Message Sent!</h3>
                    <p className="text-white/50 mb-8">Thank you for reaching out. I'll get back to you soon.</p>
                    <button onClick={() => setIsSubmitted(false)} className="px-8 py-4 bg-white/5 border border-white/20 text-white rounded-full font-semibold hover:border-[#00ff88] hover:text-[#00ff88] transition-all">Send Another Message</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div>
                      <Label htmlFor="name" className="text-white mb-3 block text-sm font-medium">Your Name <span className="text-[#00ff88]">*</span></Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" className="bg-white/5 border-white/10 text-white h-14 rounded-xl focus:border-[#00ff88]" />
                      {errors.name && <p className="text-red-400 text-sm mt-2">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white mb-3 block text-sm font-medium">Your Email <span className="text-[#00ff88]">*</span></Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="bg-white/5 border-white/10 text-white h-14 rounded-xl focus:border-[#00ff88]" />
                      {errors.email && <p className="text-red-400 text-sm mt-2">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-white mb-3 block text-sm font-medium">Your Message <span className="text-[#00ff88]">*</span></Label>
                      <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell me about your project..." rows={6} className="bg-white/5 border-white/10 text-white rounded-xl focus:border-[#00ff88]" />
                      {errors.message && <p className="text-red-400 text-sm mt-2">{errors.message}</p>}
                    </div>
                    <button type="submit" disabled={isSubmitting} className="group w-full px-8 py-5 bg-[#00ff88] text-black rounded-full font-semibold text-lg hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300 disabled:opacity-50">
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;